import { Platform, Vibration } from 'react-native';
import * as Haptics from 'expo-haptics';
import AsyncStorage from '@react-native-async-storage/async-storage';
import AsyncStorageHelper from '../services/asyncStorageHelper';

// Types de feedback disponibles
export const HAPTIC_TYPES = {
  DISABLED: 'disabled',
  LIGHT: 'light',
  MEDIUM: 'medium', 
  HEAVY: 'heavy',
  SOUND: 'sound'
};

// Configuration par défaut - changé de LIGHT à MEDIUM
const DEFAULT_HAPTIC_TYPE = HAPTIC_TYPES.MEDIUM;

class HapticService {
  constructor() {
    this.currentType = DEFAULT_HAPTIC_TYPE;
    this.isEnabled = true;
    this.loadSettings();
  }

  // Charger les paramètres sauvegardés
  async loadSettings() {
  try {
    const savedType = await AsyncStorageHelper.getString('hapticType', DEFAULT_HAPTIC_TYPE);
    const savedEnabled = await AsyncStorageHelper.getItem('hapticEnabled', true);
    
    // Validation du type
    if (savedType && Object.values(HAPTIC_TYPES).includes(savedType)) {
      this.currentType = savedType;
    } else {
      console.warn('Invalid haptic type found, using default:', savedType);
      this.currentType = DEFAULT_HAPTIC_TYPE;
    }
    
    // Validation du boolean
    if (typeof savedEnabled === 'boolean') {
      this.isEnabled = savedEnabled;
    } else if (typeof savedEnabled === 'string') {
      this.isEnabled = savedEnabled === 'true';
    } else {
      this.isEnabled = true;
    }
    
  } catch (error) {
    console.error('Erreur lors du chargement des paramètres haptiques:', error);
    // Valeurs par défaut en cas d'erreur
    this.currentType = DEFAULT_HAPTIC_TYPE;
    this.isEnabled = true;
  }
}

  // Sauvegarder les paramètres
  async saveSettings(type, enabled) {
    try {
      // Validation des paramètres
      if (!Object.values(HAPTIC_TYPES).includes(type)) {
        console.error('Invalid haptic type:', type);
        return false;
      }
      
      if (typeof enabled !== 'boolean') {
        console.error('Invalid enabled value:', enabled);
        return false;
      }
      
      const results = await Promise.all([
        AsyncStorageHelper.setItem('hapticType', type),
        AsyncStorageHelper.setItem('hapticEnabled', enabled)
      ]);
      
      if (results.every(result => result)) {
        this.currentType = type;
        this.isEnabled = enabled;
        return true;
      } else {
        console.error('Failed to save haptic settings');
        return false;
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des paramètres haptiques:', error);
      return false;
    }
  }

  // Déclencher le feedback selon le type configuré
  async trigger(intensity = 'default') {
    if (!this.isEnabled || this.currentType === HAPTIC_TYPES.DISABLED) {
      return;
    }

    try {
      if (Platform.OS === 'web') {
        // Sur le web, utiliser l'API Vibration si disponible
        if (navigator.vibrate && this.currentType !== HAPTIC_TYPES.SOUND) {
          const duration = this.getWebVibrationDuration(intensity);
          navigator.vibrate(duration);
        }
        return;
      }

      // Sur mobile, utiliser Expo Haptics ou fallback vers React Native Vibration
      switch (this.currentType) {
        case HAPTIC_TYPES.LIGHT:
          if (Haptics.impactAsync) {
            await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
          } else {
            Vibration.vibrate(50);
          }
          break;
          
        case HAPTIC_TYPES.MEDIUM:
          if (Haptics.impactAsync) {
            await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
          } else {
            Vibration.vibrate(100);
          }
          break;
          
        case HAPTIC_TYPES.HEAVY:
          if (Haptics.impactAsync) {
            await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Heavy);
          } else {
            Vibration.vibrate(150);
          }
          break;
          
        case HAPTIC_TYPES.SOUND:
          // Pour le son, on peut jouer un petit beep système
          if (Haptics.notificationAsync) {
            await Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
          }
          break;
          
        default:
          // Feedback par défaut
          if (Haptics.selectionAsync) {
            await Haptics.selectionAsync();
          } else {
            Vibration.vibrate(30);
          }
      }
    } catch (error) {
      console.error('Erreur lors du déclenchement du feedback haptique:', error);
    }
  }

  // Durée de vibration pour le web selon l'intensité
  getWebVibrationDuration(intensity) {
    switch (this.currentType) {
      case HAPTIC_TYPES.LIGHT:
        return intensity === 'strong' ? 30 : 20;
      case HAPTIC_TYPES.MEDIUM:
        return intensity === 'strong' ? 50 : 40;
      case HAPTIC_TYPES.HEAVY:
        return intensity === 'strong' ? 80 : 60;
      default:
        return 25;
    }
  }

  // Feedback spécifiques pour différentes actions
  async button() {
    await this.trigger('default');
  }

  async listItem() {
    await this.trigger('light');
  }

  async score() {
    await this.trigger('strong');
  }

  async selection() {
    await this.trigger('light');
  }

  // Nouveau : feedback pour la navigation
  async navigation() {
    await this.trigger('light');
  }

  async error() {
    if (this.isEnabled && this.currentType !== HAPTIC_TYPES.DISABLED) {
      try {
        if (Platform.OS !== 'web' && Haptics.notificationAsync) {
          await Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
        } else {
          Vibration.vibrate([100, 50, 100]);
        }
      } catch (error) {
        console.error('Erreur lors du feedback d\'erreur:', error);
      }
    }
  }

  async success() {
    if (this.isEnabled && this.currentType !== HAPTIC_TYPES.DISABLED) {
      try {
        if (Platform.OS !== 'web' && Haptics.notificationAsync) {
          await Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
        } else {
          Vibration.vibrate(50);
        }
      } catch (error) {
        console.error('Erreur lors du feedback de succès:', error);
      }
    }
  }

  // Getters pour l'état actuel
  getType() {
    return this.currentType;
  }

  getEnabled() {
    return this.isEnabled;
  }

  // Méthode pour tester le feedback depuis les paramètres
  async test() {
    const originalEnabled = this.isEnabled;
    this.isEnabled = true;
    await this.trigger('strong');
    this.isEnabled = originalEnabled;
  }

  // Reset aux valeurs par défaut
  async reset() {
    await this.saveSettings(DEFAULT_HAPTIC_TYPE, true);
  }
}

export default new HapticService();